/**
 * questions.js
 * ------------
 * Single source of truth for all assessment content.
 *
 * This file contains:
 *   - INTAKE_QUESTIONS   : The four opening fields (name, email, org, size, sector, concern)
 *   - DOMAINS            : The six assessment domains, each containing:
 *                            - metadata (id, label, colour for radar chart)
 *                            - intro text shown at the top of each domain section
 *                            - three scored questions (structural / operational / strategic)
 *                            - one perception gap question
 *
 * To update question text, edit only this file.
 * To add a domain, follow the existing pattern and update scoring.js accordingly.
 *
 * Question types map to maturity levels as follows:
 *   structural  → surfaces L1 vs L2–3 (does the thing exist?)
 *   operational → surfaces L3 vs L4   (is it consistently used?)
 *   strategic   → surfaces L4 vs L5   (is it connected to decisions?)
 *
 * Connected files:
 *   - scoring.js       uses DOMAINS to calculate per-domain scores
 *   - DomainStep.jsx   renders each domain's intro, questions and perception gap
 *   - ResultsStep.jsx  uses domain labels and colours for the radar chart
 *   - pdfGenerator.js  uses domain labels, colours and question text for the PDF
 */

// ─── Intake questions ─────────────────────────────────────────────────────────
// These appear on the first screen before the assessment begins.
// I1–I3 map to standard HubSpot contact fields (no custom properties needed).
// I4 maps to HubSpot numemployees and applies the scale modifier to output narratives.
// I6 maps to the six domains and becomes the "focus first" anchor in the results output.

export const INTAKE_QUESTIONS = [
  {
    id: "I1",
    label: "Your name",
    type: "text",
    placeholder: "First and last name",
    hubspotField: "firstname / lastname", // split on first space before sending
    required: true,
  },
  {
    id: "I2",
    label: "Work email address",
    type: "email",
    placeholder: "you@yourorganisation.com",
    hubspotField: "email",
    required: true,
  },
  {
    id: "I3",
    label: "Organisation name",
    type: "text",
    placeholder: "Your organisation",
    hubspotField: "company",
    required: true,
  },
  {
    id: "I4",
    label: "How many people work in your organisation?",
    type: "select",
    options: ["1-5", "5-25", "25-50", "50-100", "100-500", "500-1000", "1000+"],
    hubspotField: "numemployees",
    required: true,
  },
  {
    id: "I5",
    label: "Which best describes your organisation?",
    type: "select",
    options: [
      "SME / growing business",
      "Charity or not-for-profit",
      "Membership body or association",
      "Public sector",
      "Other",
    ],
    hubspotField: "industry",
    required: true,
  },
  {
    id: "I6",
    label: "Which of these feels most urgent to you right now?",
    type: "select",
    helpText: "Pick the one that resonates most — this shapes the focus of your results.",
    options: [
      "We don't have a clear digital direction",
      "We're not confident about our data or compliance",
      "We don't have the right digital skills in the team",
      "Our technology has grown without a plan",
      "Things break and we don't recover well",
      "We're not sure how to approach AI sensibly",
    ],
    // Maps to domain IDs in order: D1, D2, D3, D4, D5, D6
    domainMap: ["D1", "D2", "D3", "D4", "D5", "D6"],
    hubspotField: "cawood_primary_concern", // single custom property — the only one we create
    required: true,
  },
];

// ─── Perception gap scale ─────────────────────────────────────────────────────
// Each domain ends with a perception gap question using this scale.
// The numeric value is used in scoring.js to calculate the delta.
// A delta of +1 or greater on any domain is flagged in the HubSpot note.

export const PERCEPTION_GAP_OPTIONS = [
  { label: "Much lower",     value: -2 },
  { label: "Somewhat lower", value: -1 },
  { label: "About the same", value:  0 },
  { label: "Somewhat higher",value:  1 },
  { label: "Much higher",    value:  2 },
];

// ─── Assessment domains ───────────────────────────────────────────────────────
// Six domains. Each has three scored questions and one perception gap question.
// Colours are used on the radar chart and in the PDF.
// All brand colour values are also defined in cawood-assessment.css — if you
// update a colour, update it in both places.

export const DOMAINS = [
  {
    id: "D1",
    label: "Digital Strategy",
    color: "#FF6F61", // coral — primary brand accent
    intro: `This section is about whether your organisation has a clear, shared sense of where
it's going digitally — and whether that direction actually shapes how decisions get made.
A strong digital strategy isn't a document that lives in a drawer. It's something people
can point to, that has someone accountable for it, and that gets revisited as the
organisation changes.`,
    questions: [
      {
        id: "D1.Q1",
        type: "structural",
        text: "Our organisation has a documented digital strategy that people can actually refer to.",
        surfaces: "L1–2 vs L3+",
      },
      {
        id: "D1.Q2",
        type: "operational",
        text: "Digital priorities are consistently reflected in how decisions get made day to day, not just in planning documents.",
        surfaces: "L3 vs L4",
      },
      {
        id: "D1.Q3",
        type: "strategic",
        text: "Our digital strategy has visible ownership at board or senior leadership level and is reviewed on a regular cycle.",
        surfaces: "L4 vs L5",
      },
    ],
    perceptionGap: {
      id: "D1.PG",
      text: "How do you think the rest of your organisation would rate your digital strategy compared to your answers above?",
    },
  },

  {
    id: "D2",
    label: "Data & Governance",
    color: "#3A506B", // slate — dark text colour
    intro: `This section is about how your organisation handles the information it collects and holds.
That includes knowing what data you have and where it lives, following the rules around how
it's used and protected, and thinking about data as something genuinely useful — not just a
compliance obligation. Good data governance means your organisation could answer a subject
access request, respond to a breach, or brief a new leader on your data landscape without
having to scramble.`,
    questions: [
      {
        id: "D2.Q1",
        type: "structural",
        text: "Our organisation knows what data it holds, where it lives, and who is responsible for it.",
        surfaces: "L1–2 vs L3+",
      },
      {
        id: "D2.Q2",
        type: "operational",
        text: "Data governance practices — including how we handle subject access requests, breaches and retention — are consistently followed across the organisation, not just in one team.",
        surfaces: "L3 vs L4",
      },
      {
        id: "D2.Q3",
        type: "strategic",
        text: "Our approach to data is connected to our wider strategic priorities — including how ready we are to use data for decision-making and, where relevant, AI.",
        surfaces: "L4 vs L5",
      },
    ],
    perceptionGap: {
      id: "D2.PG",
      text: "How do you think the rest of your organisation would rate your data and governance practices compared to your answers above?",
    },
  },

  {
    id: "D3",
    label: "People & Skills",
    color: "#0CF57E", // CTA green
    intro: `This section is about whether your organisation has the digital capability it needs —
and whether you know where the gaps are. It's not just about having technical people. It's
about whether digital skills are part of how you think about recruitment, development and
succession. Organisations that get this right don't just hire for today's tools — they build
a team that can adapt as technology changes.`,
    questions: [
      {
        id: "D3.Q1",
        type: "structural",
        text: "Our organisation has a clear picture of the digital skills it currently has and the gaps that exist.",
        surfaces: "L1–2 vs L3+",
      },
      {
        id: "D3.Q2",
        type: "operational",
        text: "Digital skills development is planned and budgeted across the organisation — not left to individuals to sort out for themselves.",
        surfaces: "L3 vs L4",
      },
      {
        id: "D3.Q3",
        type: "strategic",
        text: "Digital capability is explicitly considered in recruitment, performance conversations and succession planning — not treated as separate from people strategy.",
        surfaces: "L4 vs L5",
      },
    ],
    perceptionGap: {
      id: "D3.PG",
      text: "How do you think the rest of your organisation would rate your approach to digital skills and capability compared to your answers above?",
    },
  },

  {
    id: "D4",
    label: "Technology & Architecture",
    color: "#5B7FA6", // mid blue-grey
    intro: `This section is about the tools, platforms and systems your organisation depends on —
and whether they've been chosen deliberately or accumulated over time. Most organisations find
their technology has grown without a plan: a new tool added here, a legacy system never quite
retired there. Good technology management means knowing what you have, who owns it, what it
costs, and what you'd do if it failed.`,
    questions: [
      {
        id: "D4.Q1",
        type: "structural",
        text: "We have a documented inventory of the tools, platforms and systems our organisation relies on, including who owns each one.",
        surfaces: "L1–2 vs L3+",
      },
      {
        id: "D4.Q2",
        type: "operational",
        text: "Technology decisions in our organisation are made deliberately — with a defined process — rather than accumulating tools reactively when a need arises.",
        surfaces: "L3 vs L4",
      },
      {
        id: "D4.Q3",
        type: "strategic",
        text: "Our technology choices are explicitly connected to strategic priorities — including an understanding of risk, cost and what we would do if a key system or supplier failed.",
        surfaces: "L4 vs L5",
      },
    ],
    perceptionGap: {
      id: "D4.PG",
      text: "How do you think the rest of your organisation would rate your approach to technology and architecture compared to your answers above?",
    },
  },

  {
    id: "D5",
    label: "Service Management",
    color: "#FF9E94", // soft coral
    intro: `This section is about what happens when things go wrong digitally — and how your
organisation learns from it. Every organisation experiences failures: a website goes down,
a system breaks, a process stops working. The difference between mature and immature
organisations isn't whether failures happen. It's whether there's a defined way to respond,
a clear owner, and a habit of asking "how do we stop this happening again."`,
    questions: [
      {
        id: "D5.Q1",
        type: "structural",
        text: "When something digital breaks or goes wrong, our organisation has a defined process for responding — it doesn't depend on whoever happens to be available.",
        surfaces: "L1–2 vs L3+",
      },
      {
        id: "D5.Q2",
        type: "operational",
        text: "We consistently review what went wrong after a service failure and use that to improve — rather than fixing the immediate problem and moving on.",
        surfaces: "L3 vs L4",
      },
      {
        id: "D5.Q3",
        type: "strategic",
        text: "Service reliability and digital risk are discussed at board or senior leadership level — with clear ownership and defined tolerances for what level of disruption is acceptable.",
        surfaces: "L4 vs L5",
      },
    ],
    perceptionGap: {
      id: "D5.PG",
      text: "How do you think the rest of your organisation would rate your approach to managing digital services compared to your answers above?",
    },
  },

  {
    id: "D6",
    label: "AI & Machine Learning",
    color: "#1C2541", // deep navy
    intro: `This section is about how your organisation is approaching artificial intelligence —
not whether you're using the latest tools, but whether you're doing it thoughtfully. AI is
moving faster than most organisations can keep up with, and the risk isn't just in doing too
little. It's in adopting tools without understanding the data, security or governance
implications. Good AI maturity means having a clear position, applying it consistently, and
revisiting it regularly.`,
    questions: [
      {
        id: "D6.Q1",
        type: "structural",
        text: "Our organisation has a documented position on AI — including what tools are permitted, what data can be shared with them, and who is responsible for overseeing their use.",
        surfaces: "L1–2 vs L3+",
      },
      {
        id: "D6.Q2",
        type: "operational",
        text: "Our approach to AI is consistently understood and applied across the organisation — it isn't limited to a few enthusiastic individuals or one team.",
        surfaces: "L3 vs L4",
      },
      {
        id: "D6.Q3",
        type: "strategic",
        text: "Our organisation has made deliberate decisions about where AI genuinely serves our needs — and where it does not. Our use of AI is governed, proportionate and reviewed.",
        surfaces: "L4 vs L5",
      },
    ],
    perceptionGap: {
      id: "D6.PG",
      text: "How do you think the rest of your organisation would rate your organisation's approach to AI compared to your answers above?",
    },
  },
];
